import type { ComponentProps } from "react"
import { Link } from "~/components/Link"
import { cn } from "~/lib/utils"

function Table({ className, ...props }: ComponentProps<"table">) {
	return (
		<div className="relative w-full overflow-auto">
			<table
				data-slot="table"
				className={cn("w-full caption-bottom text-sm", className)}
				{...props}
			/>
		</div>
	)
}

function TableHeader({ className, ...props }: ComponentProps<"thead">) {
	return (
		<thead
			data-slot="table-header"
			className={cn("[&_tr]:border-b", className)}
			{...props}
		/>
	)
}

function TableBody({ className, ...props }: ComponentProps<"tbody">) {
	return (
		<tbody
			data-slot="table-body"
			className={cn("[&_tr:last-child]:border-0", className)}
			{...props}
		/>
	)
}

function TableFooter({ className, ...props }: ComponentProps<"tfoot">) {
	return (
		<tfoot
			data-slot="table-footer"
			className={cn("border-t bg-muted/50 font-medium", className)}
			{...props}
		/>
	)
}

function TableRow({
	className,
	linked = false,
	...props
}: ComponentProps<"tr"> & { linked?: boolean }) {
	return (
		<tr
			data-slot="table-row"
			className={cn(
				"border-b transition-colors hover:bg-background-secondary data-[state=selected]:bg-muted",
				linked && "relative isolate focus-within:bg-background-secondary",
				className
			)}
			{...props}
		/>
	)
}

// Keep a single native link/tab stop while extending its hit area over the row.
// Use with TableRow linked for rows without other interactive controls.
function TableRowLink({ className, ...props }: ComponentProps<typeof Link>) {
	return (
		<Link
			className={cn(
				"flex items-center gap-3 font-medium outline-none after:absolute after:inset-0 after:z-10 focus-visible:after:ring-2 focus-visible:after:ring-inset focus-visible:after:ring-ring",
				className
			)}
			{...props}
		/>
	)
}

function TableHead({ className, ...props }: ComponentProps<"th">) {
	return (
		<th
			data-slot="table-head"
			className={cn(
				"h-12 px-4 text-left align-middle font-medium text-muted-foreground whitespace-nowrap has-[[role=checkbox]]:pr-0",
				className
			)}
			{...props}
		/>
	)
}

function TableCell({ className, ...props }: ComponentProps<"td">) {
	return (
		<td
			data-slot="table-cell"
			className={cn("p-4 align-middle has-[[role=checkbox]]:pr-0", className)}
			{...props}
		/>
	)
}

function TableCaption({ className, ...props }: ComponentProps<"caption">) {
	return (
		<caption
			data-slot="table-caption"
			className={cn("mt-4 text-sm text-muted-foreground", className)}
			{...props}
		/>
	)
}

function TableFrame({ className, ...props }: React.ComponentProps<"div">) {
	return (
		<div
			className={cn(
				"overflow-hidden rounded-md border border-stroke",
				className
			)}
			{...props}
		/>
	)
}

function TableStateRow({
	colSpan,
	children,
	variant = "empty"
}: {
	colSpan: number
	children: React.ReactNode
	variant?: "empty" | "loading" | "error"
}) {
	return (
		<TableRow>
			<TableCell
				colSpan={colSpan}
				className={cn(
					"text-center",
					variant === "error" ? "text-destructive" : "text-text-sub"
				)}
			>
				{children}
			</TableCell>
		</TableRow>
	)
}

export {
	TableFrame,
	TableStateRow,
	Table,
	TableBody,
	TableCaption,
	TableCell,
	TableFooter,
	TableHead,
	TableHeader,
	TableRow,
	TableRowLink
}
